import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { qualificationsApi } from '../services/backendApi';
import { sortQualificationsByIssueDate } from '../utils';
import type { Qualification, QualificationFormData } from '../types';

interface UseQualificationsReturn {
  qualifications: Qualification[];
  loading: boolean;
  error: string | null;
  fetchQualifications: () => Promise<void>;
  createQualification: (data: QualificationFormData) => Promise<{ success: boolean; id?: string; error?: string }>;
  updateQualification: (id: string, data: Partial<QualificationFormData>) => Promise<{ success: boolean; error?: string }>;
  deleteQualification: (id: string) => Promise<{ success: boolean; error?: string }>;
  clearError: () => void;
}

export const useQualifications = (): UseQualificationsReturn => {
  const { isAuthenticated } = useAuth();
  const [qualifications, setQualifications] = useState<Qualification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchQualifications = useCallback(async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    setError(null);

    try {
      const data = await qualificationsApi.getAll();
      setQualifications(sortQualificationsByIssueDate(data));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load qualifications');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchQualifications();
  }, [fetchQualifications]);

  const createQualification = useCallback(async (data: QualificationFormData) => {
    setLoading(true);
    setError(null);

    try {
      const created = await qualificationsApi.create(data);
      setQualifications(prev => sortQualificationsByIssueDate([created, ...prev]));
      return { success: true, id: created.id };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create qualification';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, []);

  const updateQualification = useCallback(async (id: string, data: Partial<QualificationFormData>) => {
    setLoading(true);
    setError(null);

    try {
      const updated = await qualificationsApi.update(id, data);
      setQualifications(prev =>
        sortQualificationsByIssueDate(prev.map(q => (q.id === id ? updated : q))),
      );
      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update qualification';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteQualification = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);

    try {
      await qualificationsApi.remove(id);
      setQualifications(prev => prev.filter(q => q.id !== id));
      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete qualification';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return {
    qualifications,
    loading,
    error,
    fetchQualifications,
    createQualification,
    updateQualification,
    deleteQualification,
    clearError,
  };
};
